import React, { useState, useEffect, useRef } from "react";
import { io } from "socket.io-client";
import { Button, Input, List, Avatar, Badge, Drawer, Typography, message as antMessage } from "antd";
import { MessageOutlined, SendOutlined, UserOutlined, ArrowLeftOutlined, WarningOutlined } from "@ant-design/icons";
import axiosInstance, { baseURL } from "../configs";
import moment from "moment";

const { Text } = Typography;

// Notification sound (base64 encoded short beep)
const NOTIFICATION_SOUND = "data:audio/wav;base64,UklGRl9vT19XQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YU";

const playNotificationSound = () => {
    try {
        const audio = new Audio("/assets/media/notification.mp3"); // Use custom sound if available
        audio.volume = 0.5;
        audio.play().catch(() => {
            // Fallback: browser may block autoplay
            console.log("Audio autoplay blocked");
        });
    } catch (e) {
        console.log("Sound not available");
    }
};

const ChatWidget = () => {
    const [visible, setVisible] = useState(false);
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState("");
    const [socket, setSocket] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);
    const [unreadCount, setUnreadCount] = useState(0);
    const [typingUsers, setTypingUsers] = useState({}); // {userId: true/false}
    const [onlineUserIds, setOnlineUserIds] = useState(new Set());
    const [showScrollTop, setShowScrollTop] = useState(false); // Track if scroll-to-top is visible

    const messagesEndRef = useRef(null);
    const selectedUserRef = useRef(null);
    const visibleRef = useRef(false);
    const typingTimeoutRef = useRef(null);

    useEffect(() => {
        selectedUserRef.current = selectedUser;
    }, [selectedUser]);

    useEffect(() => {
        visibleRef.current = visible;
    }, [visible]);

    // Track scroll position to adjust chat button position
    useEffect(() => {
        const handleScroll = () => {
            // Show scroll-to-top button when scrolled more than 300px
            setShowScrollTop(window.scrollY > 300);
        };
        window.addEventListener('scroll', handleScroll);
        handleScroll(); // Check initial position
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const userStr = localStorage.getItem("user");
        if (!userStr) return;

        const user = JSON.parse(userStr);
        setCurrentUser(user);

        const socketURL = baseURL.replace(/\/api$/, '');
        console.log("[Chat] Connecting to Socket.IO:", socketURL);

        const newSocket = io(socketURL, {
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionAttempts: 5,
            reconnectionDelay: 1000,
        });

        setSocket(newSocket);

        newSocket.on("connect", () => {
            console.log("[Chat] Socket connected:", newSocket.id);
            newSocket.emit("join", user.id_user);
        });

        newSocket.on("connect_error", (error) => {
            console.error("[Chat] Connection error:", error.message);
        });

        newSocket.on("onlineUsers", (userIds) => {
            console.log("[Chat] Online users:", userIds);
            setOnlineUserIds(new Set(userIds.map(id => String(id))));
        });

        newSocket.on("userStatus", (data) => {
            console.log("[Chat] User status update:", data);
            const strUserId = String(data.userId);
            setOnlineUserIds(prev => {
                const newSet = new Set(prev);
                if (data.status === 'online') {
                    newSet.add(strUserId);
                } else {
                    newSet.delete(strUserId);
                }
                return newSet;
            });
            setUsers(prev => prev.map(u =>
                String(u.id_user) === strUserId ? { ...u, isOnline: data.status === 'online' } : u
            ));
        });

        newSocket.on("receiveMessage", (msg) => {
            console.log("[Chat] Received message:", msg);
            const currentSelected = selectedUserRef.current;
            const isVisible = visibleRef.current;
            const msgSenderId = String(msg.senderId || msg.sender_id);
            const isSystemMessage = msg.isSystem || msgSenderId === "SYSTEM";

            // System warning messages always show in current chat
            if (isSystemMessage) {
                setMessages(prev => [...prev, msg]);
                return;
            }

            if (isVisible && currentSelected && String(currentSelected.id_user) === msgSenderId) {
                setMessages(prev => [...prev, msg]);
            } else {
                setUnreadCount(prev => prev + 1);
                playNotificationSound();
                antMessage.info({
                    content: `Pesan baru dari ${msg.senderName || 'User'}`,
                    duration: 3
                });
            }
        });

        newSocket.on("messageSent", (msg) => {
            console.log("[Chat] Message sent:", msg);
            const currentSelected = selectedUserRef.current;
            const msgReceiverId = String(msg.receiverId || msg.receiver_id);
            if (currentSelected && String(currentSelected.id_user) === msgReceiverId) {
                setMessages(prev => [...prev, msg]);
            }
        });

        newSocket.on("userTyping", (data) => {
            console.log("[Chat] User typing:", data);
            setTypingUsers(prev => ({
                ...prev,
                [String(data.userId)]: data.isTyping
            }));
        });

        return () => {
            console.log("[Chat] Disconnecting socket");
            newSocket.disconnect();
        };
    }, []);

    useEffect(() => {
        if (visible) {
            fetchUsers();
            setUnreadCount(0);
        }
    }, [visible]);

    useEffect(() => {
        if (selectedUser) {
            fetchMessages(selectedUser.id_user);
        }
    }, [selectedUser]);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    const fetchUsers = async () => {
        try {
            const res = await axiosInstance.get("/chat/users");
            const usersWithOnline = res.data.map(u => ({
                ...u,
                isOnline: onlineUserIds.has(String(u.id_user))
            }));
            setUsers(usersWithOnline);
        } catch (err) {
            console.error("[Chat] Error fetching users:", err);
        }
    };

    const fetchMessages = async (otherId) => {
        try {
            const res = await axiosInstance.get(`/chat/messages/${otherId}`);
            setMessages(res.data);
        } catch (err) {
            console.error("[Chat] Error fetching messages:", err);
        }
    };

    const handleTyping = (e) => {
        setNewMessage(e.target.value);

        if (socket && selectedUser) {
            // Send typing indicator
            socket.emit("typing", {
                senderId: currentUser.id_user,
                receiverId: selectedUser.id_user,
                isTyping: true
            });

            // Clear previous timeout
            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
            }

            // Stop typing after 2 seconds of no input
            typingTimeoutRef.current = setTimeout(() => {
                socket.emit("typing", {
                    senderId: currentUser.id_user,
                    receiverId: selectedUser.id_user,
                    isTyping: false
                });
            }, 2000);
        }
    };

    const handleSend = () => {
        if (!newMessage.trim() || !selectedUser || !socket) return;

        console.log("[Chat] Sending message to:", selectedUser.id_user);
        socket.emit("sendMessage", {
            senderId: currentUser.id_user,
            receiverId: selectedUser.id_user,
            message: newMessage.trim(),
        });
        setNewMessage("");

        // Stop typing indicator
        if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
        }
        socket.emit("typing", {
            senderId: currentUser.id_user,
            receiverId: selectedUser.id_user,
            isTyping: false
        });
    };

    const formatTime = (timestamp) => {
        if (!timestamp) return "";
        return moment(timestamp).local().format("HH:mm");
    };

    const isUserTyping = selectedUser && typingUsers[String(selectedUser.id_user)];

    const renderUserList = () => (
        <List
            itemLayout="horizontal"
            dataSource={users}
            locale={{ emptyText: "Tidak ada user lain" }}
            renderItem={(user) => {
                const isOnline = onlineUserIds.has(String(user.id_user));
                return (
                    <List.Item
                        onClick={() => setSelectedUser({ ...user, isOnline })}
                        style={{
                            cursor: 'pointer',
                            padding: '14px 18px',
                            background: selectedUser?.id_user === user.id_user ? '#e6f7ff' : 'transparent',
                            borderBottom: '1px solid #f0f0f0',
                            transition: 'background 0.2s'
                        }}
                    >
                        <List.Item.Meta
                            avatar={
                                <Badge dot status={isOnline ? "success" : "default"} offset={[-4, 32]}>
                                    <Avatar
                                        icon={<UserOutlined />}
                                        style={{ backgroundColor: isOnline ? '#52c41a' : '#bfbfbf' }}
                                        size={44}
                                    />
                                </Badge>
                            }
                            title={<Text strong style={{ fontSize: 15 }}>{user.name || user.username}</Text>}
                            description={
                                <Text type={isOnline ? "success" : "secondary"} style={{ fontSize: 12 }}>
                                    {isOnline ? "● Online sekarang" : "○ Offline"}
                                </Text>
                            }
                        />
                    </List.Item>
                );
            }}
        />
    );

    const renderChatWindow = () => {
        const isOnline = onlineUserIds.has(String(selectedUser.id_user));

        return (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                {/* Header */}
                <div style={{
                    padding: '14px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    borderBottom: '1px solid #e8e8e8',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: '#fff'
                }}>
                    <Button
                        icon={<ArrowLeftOutlined style={{ color: '#fff' }} />}
                        type="text"
                        onClick={() => setSelectedUser(null)}
                        style={{ marginRight: 12 }}
                    />
                    <Avatar
                        icon={<UserOutlined />}
                        style={{ marginRight: 12, backgroundColor: isOnline ? '#52c41a' : '#bfbfbf' }}
                        size={40}
                    />
                    <div style={{ flex: 1 }}>
                        <Text strong style={{ color: '#fff', fontSize: 15 }}>
                            {selectedUser.name || selectedUser.username}
                        </Text>
                        <br />
                        <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 11 }}>
                            {isUserTyping ? "Sedang mengetik..." : (isOnline ? "● Online" : "○ Offline")}
                        </Text>
                    </div>
                </div>

                {/* Messages */}
                <div style={{ flex: 1, overflowY: 'auto', padding: 16, background: '#f0f2f5' }}>
                    {messages.length === 0 && (
                        <div style={{ textAlign: 'center', color: '#999', marginTop: 60, fontSize: 13 }}>
                            💬 Mulai percakapan dengan mengirim pesan!
                        </div>
                    )}
                    {messages.map((msg, index) => {
                        const isMe = String(msg.senderId || msg.sender_id) === String(currentUser.id_user);
                        const isSystem = msg.isSystem || msg.senderId === "SYSTEM" || msg.sender_id === "SYSTEM";

                        // System warning message style - simple red text with icon, multi-line
                        if (isSystem) {
                            return (
                                <div
                                    key={msg.id || index}
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        marginBottom: 12,
                                        marginTop: 8,
                                        padding: '8px 12px',
                                        width: '100%'
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                                        <WarningOutlined style={{ color: '#ff4d4f', fontSize: 14, marginRight: 6 }} />
                                        <Text style={{ color: '#ff4d4f', fontSize: 12, fontWeight: 600 }}>
                                            Peringatan dari System:
                                        </Text>
                                    </div>
                                    <Text style={{ color: '#ff4d4f', fontSize: 11, textAlign: 'center' }}>
                                        "Kalo ngomong yang sopan!!"
                                    </Text>
                                    <Text style={{ color: '#ff4d4f', fontSize: 11, textAlign: 'center' }}>
                                        "Buruan minta maaf!"
                                    </Text>
                                </div>
                            );
                        }

                        // Regular message
                        return (
                            <div
                                key={msg.id || index}
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: isMe ? 'flex-end' : 'flex-start',
                                    marginBottom: 12
                                }}
                            >
                                <div style={{
                                    maxWidth: '70%',
                                    padding: '10px 14px',
                                    borderRadius: 18,
                                    background: isMe ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#ffffff',
                                    color: isMe ? '#fff' : '#333',
                                    boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                                    borderBottomRightRadius: isMe ? 4 : 18,
                                    borderBottomLeftRadius: isMe ? 18 : 4,
                                    fontSize: 14
                                }}>
                                    {msg.message}
                                </div>
                                <Text type="secondary" style={{ fontSize: 10, marginTop: 4, paddingLeft: 6, paddingRight: 6 }}>
                                    {formatTime(msg.created_at)}
                                </Text>
                            </div>
                        )
                    })}
                    {isUserTyping && (
                        <div style={{ display: 'flex', alignItems: 'center', marginTop: 8 }}>
                            <div style={{
                                background: '#e4e6eb',
                                padding: '10px 14px',
                                borderRadius: 18,
                                fontSize: 13,
                                color: '#666'
                            }}>
                                <span className="typing-indicator">●●●</span>
                            </div>
                        </div>
                    )}
                    <div ref={messagesEndRef} />
                </div>

                {/* Input Area - Fixed Layout */}
                <div style={{
                    padding: '12px 16px',
                    borderTop: '1px solid #e8e8e8',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    background: '#fff',
                    position: 'relative',
                    zIndex: 10
                }}>
                    <Input
                        value={newMessage}
                        onChange={handleTyping}
                        onPressEnter={handleSend}
                        placeholder="Ketik pesan..."
                        style={{
                            flex: '1 1 auto',
                            minWidth: 0,
                            borderRadius: 24,
                            paddingLeft: 18,
                            paddingRight: 18,
                            border: '1px solid #d9d9d9',
                            height: 44
                        }}
                        size="large"
                    />
                    <Button
                        type="primary"
                        shape="circle"
                        icon={<SendOutlined />}
                        onClick={handleSend}
                        disabled={!newMessage.trim()}
                        style={{
                            flexShrink: 0,
                            background: newMessage.trim() ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#d9d9d9',
                            border: 'none',
                            width: 44,
                            height: 44,
                            minWidth: 44,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    />
                </div>
            </div>
        );
    };

    if (!currentUser) return null;

    return (
        <>
            <style>
                {`
                    @keyframes typing {
                        0%, 60%, 100% { opacity: 0.3; }
                        30% { opacity: 1; }
                    }
                    .typing-indicator span {
                        animation: typing 1.4s infinite;
                    }
                `}
            </style>

            {/* Floating Button - hidden when drawer is open */}
            {!visible && (
                <div style={{
                    position: "fixed",
                    bottom: showScrollTop ? 90 : 24,
                    right: 24,
                    zIndex: 9999,
                    transition: 'bottom 0.3s ease'
                }}>
                    <Badge count={unreadCount} offset={[-6, 6]} style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>
                        <Button
                            type="primary"
                            shape="circle"
                            icon={<MessageOutlined style={{ fontSize: 24 }} />}
                            style={{
                                width: 56,
                                height: 56,
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                border: 'none',
                                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.5)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            onClick={() => setVisible(true)}
                        />
                    </Badge>
                </div>
            )}

            {/* Chat Drawer */}
            <Drawer
                title={!selectedUser ? "💬 Ngomong Langsung Deh..." : null}
                placement="right"
                closable={true}
                onClose={() => { setVisible(false); setSelectedUser(null); }}
                visible={visible}
                width={Math.min(380, window.innerWidth - 20)}
                bodyStyle={{ padding: 0, height: 'calc(100% - 55px)', display: 'flex', flexDirection: 'column' }}
                headerStyle={{ display: selectedUser ? 'none' : 'flex', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff' }}
            >
                {!selectedUser ? renderUserList() : renderChatWindow()}
            </Drawer>
        </>
    );
};

export default ChatWidget;
